// CategoryManager.tsx
import React, { useMemo, useState } from 'react';

import { Group, Stack, Title } from '@mantine/core';
import { IconArrowLeft, IconInfoSquareRounded, IconPlus } from '@tabler/icons-react';
import Drawer from 'components/Drawer';
import KiloActionIcon from 'components/KiloActionIcon';
import KiloButton from 'components/KiloButton';
import KiloTooltip, { TOOLTIP_MESSAGES } from 'components/KiloTooltip';
import useRevenueCategories from 'hooks/revenueCategory/useRevenueCategories';
import Loading from 'pages/shared/Loading';
import { RevenueCategory } from 'types';

import RevenueCategoryForm from './RevenueCategoryForm';

const RevenueCategoryManager = ({ permission = true }: { permission?: boolean }): JSX.Element => {
  const { data: categories, isLoading } = useRevenueCategories();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [createCategoryIsOpen, setCreateCategoryIsOpen] = useState<boolean>(false);

  const handleAddCategory = (): void => {
    if (createCategoryIsOpen) return;
    setCreateCategoryIsOpen(true);
  };

  const allCategories: RevenueCategory[] | [] = useMemo(() => {
    if (!categories || !categories.data) return [];

    return categories.data;
  }, [categories]);

  const handleCloseAndReset = (): void => {
    setIsOpen(false);
  };

  const handleLocalFromClose = (): void => {
    setCreateCategoryIsOpen(false);
  };

  const handleCategoryFormOpen = (): void => {
    setIsOpen(true);
  };

  if (isLoading) return <Loading />;

  return (
    <Group grow position='left' align='center'>
      <KiloButton
        rootVariantType='flat'
        disabled={!permission}
        variant='subtle'
        onClick={handleCategoryFormOpen}
      >
        Manage Revenue Categories
      </KiloButton>
      <Drawer
        opened={isOpen}
        onClose={handleCloseAndReset}
        title={<Title order={3}>Revenue Categorization</Title>}
        size='xl'
        pt={'1rem !important'}
      >
        <KiloButton
          rootVariantType='revenueCategoriesGray'
          disabled={!permission}
          variant='subtle'
          onClick={handleCloseAndReset}
        >
          <IconArrowLeft size='1.4rem' /> Back
        </KiloButton>
        <Group mb={'1.2rem'}>
          <Title order={4} weight={600}>
            Manage Revenue Categories
          </Title>
          <KiloTooltip
            wrapperDivClassVariantType='defaultAbsolute'
            label={TOOLTIP_MESSAGES.CATEGORIES_EXTRA_INFO}
            withArrow
            position='left-start'
          >
            <KiloActionIcon rootVariantType='defaultAbsolute' variant='subtle'>
              <IconInfoSquareRounded size='1.4rem' />
            </KiloActionIcon>
          </KiloTooltip>
        </Group>
        <Stack spacing={'1.2rem'}>
          {allCategories.map(category => (
            <RevenueCategoryForm
              setCreateCategoryIsOpen={setCreateCategoryIsOpen}
              key={category.revenueCategoryId}
              category={category}
              permission={permission}
            />
          ))}
          {createCategoryIsOpen && (
            <RevenueCategoryForm
              setCreateCategoryIsOpen={setCreateCategoryIsOpen}
              permission={permission}
              onClose={handleLocalFromClose}
            />
          )}
          <Group position='left' noWrap>
            <KiloButton
              rootVariantType='flat'
              disabled={!permission || createCategoryIsOpen}
              variant='subtle'
              onClick={handleAddCategory}
            >
              Add New Category <IconPlus size={16} />
            </KiloButton>
          </Group>
        </Stack>
      </Drawer>
    </Group>
  );
};

export default RevenueCategoryManager;
